import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChartLineUp, MagnifyingGlass, Bank, Receipt, FileText, Robot, SignOut, Sun, Moon, List, X } from '@phosphor-icons/react';

const Layout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const navItems = [
    { path: '/', icon: ChartLineUp, label: 'Dashboard' },
    { path: '/discovery', icon: MagnifyingGlass, label: 'Discovery' },
    { path: '/grants', icon: Bank, label: 'Grants' },
    { path: '/expenses', icon: Receipt, label: 'Expenses' },
    { path: '/reports', icon: FileText, label: 'Reports' },
    { path: '/ai-copilot', icon: Robot, label: 'AI Copilot' },
  ];

  return (
    <div className="flex h-screen bg-background text-foreground transition-colors duration-300">
      {/* Mobile Backdrop */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden" 
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border flex flex-col transition-transform duration-300 ease-in-out transform
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:relative lg:translate-x-0
      `}>
        <div className="p-6 border-b border-border flex justify-between items-center">
          <div className="flex flex-col items-center text-center gap-4 w-full">
            <div className="p-2 bg-[#1a1a1a] rounded-lg">
              <img 
                src="/logo.png" 
                alt="Equality California" 
                className="h-16 w-auto object-contain"
              />
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tighter uppercase">EQCA Ops</h1>
            </div>
          </div>
          <button className="lg:hidden p-2 text-muted-foreground" onClick={() => setIsMobileMenuOpen(false)}>
            <X size={24} />
          </button>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-[#E04E39] text-white shadow-lg'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon size={20} weight={isActive ? "bold" : "regular"} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 space-y-4">
          <button
            onClick={toggleTheme}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-medium bg-muted text-foreground hover:bg-muted/80 transition-all border border-border"
          >
            {theme === 'dark' ? (
              <>
                <Sun size={20} weight="fill" className="text-yellow-500" />
                Light Mode
              </>
            ) : (
              <>
                <Moon size={20} weight="fill" className="text-blue-500" />
                Dark Mode
              </>
            )}
          </button>
        </div>

        <div className="p-4 border-t border-border text-center">
          <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">VANCO AI</p>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <header className="lg:hidden bg-card border-b border-border p-4 flex justify-between items-center sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-[#1a1a1a] rounded-md">
              <img src="/logo.png" alt="EQCA" className="h-8 w-auto" />
            </div>
            <span className="font-black text-sm tracking-tighter uppercase">EQCA Ops</span>
          </div>
          <button 
            className="p-2 bg-muted rounded-md text-foreground"
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <List size={24} />
          </button>
        </header>

        <main className="flex-1 overflow-auto bg-background transition-colors duration-300">
          <div className="h-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
